// =============================================================================
// CHANGE LOG — Auth system overhaul  (May 2026)
// =============================================================================
//
// PROBLEMS FIXED:
//   1. "Open new tab / come back after 2h → must log in again" — even with a
//      valid 30-day refresh token cookie in the browser.
//   2. Random logouts when 2+ tabs were open simultaneously.
//   3. Mid-session logout at exactly the 2-hour access-token expiry mark.
//   4. Logging in on Tab B instantly logged out Tab A.
//   5. Anonymous → registered conversion left no refresh token, so those users
//      were permanently logged out after 2 hours.
//
// WHAT CHANGED IN THIS FILE:
//
//   A. Proactive expiry check in initializeAuth (line ~180):
//      OLD: grabbed the stored token and immediately fired GET /api/auth/users/me.
//           If the token was expired the server returned 401, THEN a refresh was
//           attempted.  If that refresh failed for any reason → redirect to /login.
//      NEW: decode the JWT client-side first.  If expired, call /api/auth/refresh
//           BEFORE making any authenticated request.  App loads normally without
//           the user ever seeing the login page.
//
//   B. Background refresh timer — useEffect on [token] (line ~69):
//      After every login or token refresh, a setTimeout is scheduled to fire
//      5 minutes before the access token expires.  It silently calls
//      /api/auth/refresh in the background.  Users who stay in a tab for days
//      will never hit an expired token mid-session.
//      Timer is cancelled on logout and re-scheduled whenever the token changes.
//
//   C. Cross-tab sync via localStorage 'storage' events (line ~111):
//      storage events fire in every OTHER tab when localStorage changes.
//      - Another tab sets 'token' (login / refresh) → adopt the new token here,
//        reschedule the refresh timer.  Tab stays logged in.
//      - Another tab removes 'token' (logout) → propagate logout to this tab.
//
//   D. login() no longer calls localStorage.removeItem('token') (line ~252):
//      CRITICAL BUG FIXED: removeItem fires a storage event in other tabs.
//      The cross-tab listener (fix C) saw newValue=null and treated it as a
//      logout, redirecting all other open tabs to /login the moment a login
//      started in any one tab.  The setItem call after the API response
//      overwrites the old token directly — no remove needed.
//
// IF SOMETHING BREAKS HERE, CHECK:
//   - Background timer: search for "refreshTimer" — it must be cleared in logout
//     and re-scheduled when token changes via the useEffect([token]) block.
//   - Cross-tab logout not propagating: the 'storage' event only fires in OTHER
//     tabs.  The tab that calls removeItem does NOT receive its own event.
//   - Proactive refresh failing on boot: check that the refresh_token cookie
//     is present in DevTools → Application → Cookies.  If it's missing, the
//     backend never set it (e.g. /api/auth/convert-anonymous was missing the
//     Set-Cookie call — that's also fixed in backend/api/auth_api.py).
// =============================================================================

import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import api, { setAuthCallbacks, isTokenExpired, getTokenExpiry } from '../services/api';

const AuthContext = createContext(null);

// How many milliseconds before expiry to trigger the background refresh.
const REFRESH_BEFORE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

export const AuthProvider = ({ children }) => {
  const [user, setUser]       = useState(null);
  const [token, setToken]     = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [isAnonymous, setIsAnonymous] = useState(false);

  const navigate     = useNavigate();
  const queryClient  = useQueryClient();
  const refreshTimer = useRef(null);

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  const clearRefreshTimer = () => {
    if (refreshTimer.current) {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
  };

  const extractErrorMessage = (error, defaultMessage = 'An error occurred') => {
    if (!error.response?.data?.detail) return defaultMessage;
    const detail = error.response.data.detail;
    if (Array.isArray(detail)) {
      return detail
        .map((err) => {
          const field = err.loc?.length > 1 ? err.loc[err.loc.length - 1] : '';
          return `${field ? `${field}: ` : ''}${err.msg}`;
        })
        .join(', ');
    }
    if (typeof detail === 'string') return detail;
    return defaultMessage;
  };

  const fetchUserProfile = async () => {
    try {
      const { data } = await api.get('/api/auth/users/me');
      setUser(data);
      if (data.is_anonymous && data.id) {
        localStorage.setItem('anonymous_user_id', data.id);
      }
      setLoading(false);
    } catch (error) {
      console.error('[Auth] Failed to fetch user profile', error);
      // The response interceptor handles 401 → refresh → retry automatically.
      // If that also fails the interceptor clears the token and redirects.
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Background token refresh
  //
  // Scheduled whenever `token` changes to a valid, non-null value.
  // Fires 5 minutes before expiry so users never experience a mid-session 401.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    clearRefreshTimer();

    if (!token || isTokenExpired(token)) return;

    const expiry = getTokenExpiry(token);
    if (!expiry) return;

    // Cap the buffer at half the remaining lifetime so very short access
    // tokens (e.g. dev testing with 5-minute tokens) don't trigger an
    // immediate-refresh loop on every login.
    //   120-min token → buffer = 5 min  → refresh at 115 min
    //     5-min token → buffer = 2.5 min → refresh at 2.5 min
    const lifetime = expiry - Date.now();
    const buffer = Math.min(REFRESH_BEFORE_EXPIRY_MS, lifetime / 2);
    const delay = lifetime - buffer;

    const doBackgroundRefresh = async () => {
      try {
        const { data } = await api.post('/api/auth/refresh');
        const newToken = data.access_token;
        localStorage.setItem('token', newToken);
        setToken(newToken);
        api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      } catch (err) {
        // Swallow silently — the next API call will still trigger the
        // interceptor refresh path if the token is truly expired.
        console.warn('[Auth] Background token refresh failed:', err?.response?.status);
      }
    };

    if (delay <= 0) {
      // Token expires very soon (or already past the refresh window); refresh immediately.
      doBackgroundRefresh();
    } else {
      refreshTimer.current = setTimeout(doBackgroundRefresh, delay);
    }

    return clearRefreshTimer;
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------------
  // Cross-tab synchronisation via localStorage events
  //
  // storage events fire in every OTHER tab when localStorage changes.
  // This covers two scenarios:
  //   1. Another tab refreshed the token → adopt the new token here.
  //   2. Another tab logged out     → propagate the logout here.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key !== 'token') return;

      if (event.newValue) {
        // Another tab obtained a fresh token (e.g. after background refresh).
        setToken(event.newValue);
        api.defaults.headers.common['Authorization'] = `Bearer ${event.newValue}`;
      } else {
        // Another tab cleared the token (logout).
        clearRefreshTimer();
        setToken(null);
        setUser(null);
        queryClient.clear();
        navigate('/login');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [navigate, queryClient]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------------
  // Wire up the api.js interceptor callbacks
  // ---------------------------------------------------------------------------
  useEffect(() => {
    setAuthCallbacks({
      onTokenRefreshed: (newToken) => {
        // setToken triggers the background-refresh useEffect to reschedule the timer.
        setToken(newToken);
      },
      onTokenRefreshFailed: () => {
        clearRefreshTimer();
        setToken(null);
        setUser(null);
        // Navigation is handled by the interceptor (window.location.href = '/login').
      },
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------------
  // Anonymous user detection
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (user) setIsAnonymous(user.is_anonymous === true);
  }, [user]);

  // ---------------------------------------------------------------------------
  // App initialisation — runs once on mount
  //
  // Priority order:
  //   1. Stored access token (check expiry, proactively refresh if expired)
  //   2. Anonymous user session (campaign routes only)
  //   3. Trial mode (?trial=true)
  //   4. Not authenticated
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken    = localStorage.getItem('token');
      const anonymousUserId = localStorage.getItem('anonymous_user_id');
      const urlParams      = new URLSearchParams(window.location.search);
      const isTrial        = urlParams.get('trial') === 'true';
      const currentPath    = window.location.pathname;
      const isCampaignRoute =
        currentPath === '/campaign/new' || currentPath.startsWith('/campaign/');

      if (storedToken) {
        setToken(storedToken);

        if (isTokenExpired(storedToken)) {
          // Access token is expired — attempt a proactive refresh before
          // making any authenticated request.  This avoids the 401 round-trip
          // and is the primary fix for "open new tab → must log in again".
          try {
            const { data } = await api.post('/api/auth/refresh');
            const refreshedToken = data.access_token;

            localStorage.setItem('token', refreshedToken);
            setToken(refreshedToken);
            api.defaults.headers.common['Authorization'] = `Bearer ${refreshedToken}`;

            await fetchUserProfile();
          } catch {
            // Refresh failed (cookie missing or blacklisted).
            // Last-chance: another tab may have refreshed while we were waiting.
            const latestToken = localStorage.getItem('token');
            if (latestToken && !isTokenExpired(latestToken)) {
              setToken(latestToken);
              api.defaults.headers.common['Authorization'] = `Bearer ${latestToken}`;
              await fetchUserProfile();
            } else {
              localStorage.removeItem('token');
              setToken(null);
              setLoading(false);
            }
          }
        } else {
          // Token is still valid — fetch profile directly.
          // If the server rejects it (e.g. edge-case clock skew) the
          // response interceptor will transparently refresh and retry.
          await fetchUserProfile();
        }

      } else if (anonymousUserId) {
        if (!isCampaignRoute) {
          localStorage.removeItem('anonymous_user_id');
          setLoading(false);
        } else {
          await fetchUserProfile();
        }

      } else if (isTrial) {
        await createTrialUser();

      } else {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------------
  // Auth actions
  // ---------------------------------------------------------------------------

  const createTrialUser = async () => {
    try {
      const { data } = await api.post('/api/auth/create-trial-user');
      localStorage.setItem('anonymous_user_id', data.anonymous_user_id);
      setUser(data.user);
      setIsAnonymous(true);
      setLoading(false);
      return data;
    } catch (error) {
      console.error('[Auth] Failed to create trial user', error);
      setLoading(false);
      throw error;
    }
  };

  const login = async (email, password) => {
    try {
      // Do NOT remove the token from localStorage here.
      // localStorage.removeItem fires a storage event in every other tab,
      // which our cross-tab listener interprets as a logout and redirects
      // those tabs to /login.  The setItem below overwrites it cleanly.
      queryClient.clear();

      const { data } = await api.post('/api/auth/login', { email, password });

      localStorage.setItem('token', data.access_token);
      setToken(data.access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;

      await fetchUserProfile();

      return { success: true, needsOnboarding: data.needs_onboarding ?? false };
    } catch (error) {
      console.error('[Auth] Login failed', error);
      return { success: false, message: extractErrorMessage(error, 'Login failed') };
    }
  };

  const register = async (userData) => {
    try {
      await api.post('/api/auth/register', userData);
      const loginResult = await login(userData.email, userData.password);
      if (loginResult.success) return { success: true, redirectTo: '/' };
      return loginResult;
    } catch (error) {
      console.error('[Auth] Registration failed', error);
      return { success: false, message: extractErrorMessage(error, 'Registration failed') };
    }
  };

  const logout = async () => {
    clearRefreshTimer();

    try {
      await api.post('/api/auth/logout');
    } catch (error) {
      console.error('[Auth] Logout API call failed', error);
    } finally {
      queryClient.clear();
      localStorage.removeItem('token');
      localStorage.removeItem('anonymous_user_id');
      setToken(null);
      setUser(null);
      navigate('/login');
    }
  };

  const convertAnonymousToRegistered = async (email, password, firstName, lastName) => {
    const anonymousUserId = localStorage.getItem('anonymous_user_id');
    try {
      const { data } = await api.post('/api/auth/convert-anonymous', {
        email,
        password,
        confirm_password: password,
        first_name: firstName,
        last_name: lastName,
        anonymous_user_id: anonymousUserId,
      });

      localStorage.setItem('token', data.access_token);
      localStorage.removeItem('anonymous_user_id');
      setToken(data.access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;
      setIsAnonymous(false);

      await fetchUserProfile();

      return { success: true, user: data };
    } catch (error) {
      return { success: false, message: extractErrorMessage(error, 'Conversion failed') };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        isAnonymous,
        login,
        register,
        logout,
        fetchUserProfile,
        convertAnonymousToRegistered,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
